import { useEffect, useRef } from 'react'

type ElementEventType<K> =
  | HTMLElementEventMap[K & keyof HTMLElementEventMap]
  | DocumentEventMap[K & keyof DocumentEventMap]
  | WindowEventMap[K & keyof WindowEventMap]
function useEventListener<
  K extends keyof (HTMLElementEventMap & DocumentEventMap & WindowEventMap),
>(
  eventType: K,
  handler: (event: ElementEventType<K>) => void,
  element: HTMLElement | Document | Window | null = window,
) {
  const savedHandler = useRef(handler)

  useEffect(() => {
    savedHandler.current = handler
  }, [handler])

  useEffect(() => {
    if (!element) return

    const eventListener = (event: Event) =>
      savedHandler.current(event as ElementEventType<K>)

    element.addEventListener(eventType, eventListener)

    return () => {
      element.removeEventListener(eventType, eventListener)
    }
  }, [eventType, element])
}

export default useEventListener
